/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/navigation/BottomTabNavigation.tsx
 * PURPOSE: Mobile bottom navigation bar với thumb-zone optimization
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: UI Components, Vietnamese F&B User Experience
 * - Impact: User Experience, Interface Quality, Accessibility
 * - Stakeholders: End Users, UI/UX Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, UI Components
 * - Pattern: React Component, Design System, Reusable Components
 * - Dependencies: React 18, TypeScript, CSS Variables (design-system.css)
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/LAYOUT-NAVIGATION-DESIGN.md
 * - Mockup: docs/02-design/04-ui-ux-design/NAVIGATION-FLOW-MOCKUP.html
 * - CSS: frontend/src/styles/design-system.css
 *  */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationItem } from '@/types/navigation';
import { isNavigationItemActive, mobileNavigationUtils } from '@/utils/navigation';
import { NavigationItemComponent } from './NavigationItem';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import styles from './BottomTabNavigation.module.css';

interface BottomTabNavigationProps {
  items: NavigationItem[];
  currentPath: string;
}

export const BottomTabNavigation: React.FC<BottomTabNavigationProps> = ({
  items,
  currentPath
}) => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Limit to maximum 5 items for optimal mobile UX
  const displayItems = items.slice(0, 5);
  
  // Find current active tab index
  const currentActiveIndex = displayItems.findIndex(item => 
    isNavigationItemActive(item, currentPath)
  );

  // Handle tap/click on a tab — mirrors SideNavigation.tsx's handleItemClick
  // (same useNavigate() instance / react-router API as handleTabSwipe below).
  // ISS-EMP-10: this call was missing entirely, so tapping any bottom-nav
  // tab (including the raised center "Chấm công" button) fired the gtag
  // analytics event but never changed route.
  const handleTabClick = useCallback((item: NavigationItem, index: number) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'mobile_tab_click', {
        tab_name: item.label,
        tab_path: item.path,
        tab_index: index
      });
    }

    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
  }, [navigate]);

  // Handle swipe gestures for tab navigation
  const handleTabSwipe = useCallback((direction: 'left' | 'right') => {
    if (isAnimating || displayItems.length <= 1) return;
    
    let nextIndex: number;
    
    if (direction === 'left' && currentActiveIndex < displayItems.length - 1) {
      nextIndex = currentActiveIndex + 1;
    } else if (direction === 'right' && currentActiveIndex > 0) {
      nextIndex = currentActiveIndex - 1;
    } else {
      return; // No valid navigation
    }

    setIsAnimating(true);
    navigate(displayItems[nextIndex].path);
    
    // Reset animation state after transition
    setTimeout(() => setIsAnimating(false), 300);
  }, [currentActiveIndex, displayItems, navigate, isAnimating]);

  // Swipe gesture handlers
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => handleTabSwipe('left'),
    onSwipeRight: () => handleTabSwipe('right'),
  }, {
    threshold: 80, // Increased threshold for tab switching
    maxTime: 500,
    minVelocity: 0.5,
    preventDefault: false // Allow other touch interactions
  });

  // Per LAYOUT-NAVIGATION-DESIGN.md - Mobile Bottom Navigation
  // Static styling (position/background/border/shadow/safe-area) lives in
  // BottomTabNavigation.module.css (web-sync checklist A.17); only the
  // animation-dependent transition stays inline.
  return (
    <div
      {...swipeHandlers}
      className={styles.container}
      style={{
        transition: isAnimating ? 'all 0.3s' : undefined,
      }}
      role="tablist"
      aria-label="Main navigation"
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        maxWidth: '448px',
        margin: '0 auto',
      }}>
        {displayItems.map((item, index) => {
          const isActive = isNavigationItemActive(item, currentPath);

          return (
            <NavigationItemComponent
              key={item.id || item.path}
              item={{
                ...item,
                // Use mobile-optimized labels
                label: mobileNavigationUtils.getMobileLabel(item.label)
              }}
              isActive={isActive}
              variant="bottomTab"
              onClick={() => handleTabClick(item, index)}
            />
          );
        })}
      </div>

      {/* Swipe indicator dots */}
      {displayItems.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          paddingTop: '4px',
          paddingBottom: '4px',
        }}>
          {displayItems.map((_, index) => (
            <div
              key={index}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: index === currentActiveIndex
                  ? 'var(--primary-600)'
                  : 'var(--gray-300)',
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>
      )}

      {/* Screen reader announcements */}
      <div style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }} aria-live="polite">
        Navigation changed to {
          displayItems.find(item =>
            isNavigationItemActive(item, currentPath)
          )?.label || 'Unknown'
        }
      </div>

      {/* Swipe hint for first-time users */}
      {displayItems.length > 1 && (
        <div style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}>
          Vuốt trái phải để chuyển tab
        </div>
      )}
    </div>
  );
};